import { useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Plus, LayoutGrid, Clock, Search, X, Globe, Lock, Archive, RotateCcw } from 'lucide-react'
import { PageShell } from '@/components/common/PageShell'
import { Button } from '@/components/ui'
import { SectionLoader } from '@/components/common/SectionLoader'
import { useBoards, useUpdateBoard } from '../hooks/useBoards'
import { cn } from '@/lib/cn'
import { getAvatarColorClass } from '@/components/common/Avatar'
import { useMe } from '@/features/settings/hooks/useMe'
import { CreateBoardModal } from './CreateBoardModal'
import { useOpenFromQuery } from '@/hooks/useOpenFromQuery'

const DEFAULT_BOARD_COVERS = [
  'linear-gradient(135deg, #e0f2fe 0%, #eff6ff 54%, #e0e7ff 100%)',
  'linear-gradient(135deg, #d1fae5 0%, #ecfdf5 54%, #cffafe 100%)',
  'linear-gradient(135deg, #ede9fe 0%, #fdf4ff 54%, #f3e8ff 100%)',
  'linear-gradient(135deg, #fef3c7 0%, #fff7ed 54%, #ffe4e6 100%)',
  'linear-gradient(135deg, #e2e8f0 0%, #f8fafc 54%, #e4e4e7 100%)',
  'linear-gradient(135deg, #cffafe 0%, #f0f9ff 54%, #dbeafe 100%)',
]

function getDefaultBoardCover(idx: number): string {
  return DEFAULT_BOARD_COVERS[idx % DEFAULT_BOARD_COVERS.length]
}

export function BoardsPage(): React.ReactElement {
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const [searchQuery, setSearchQuery] = useState('')
  const showArchived = pathname === '/boards/archived'
  const { data: currentUser } = useMe()
  const { data: boards, isLoading, error } = useBoards(showArchived)
  const updateBoard = useUpdateBoard()
  const [showCreate, setShowCreate] = useState(false)
  useOpenFromQuery(setShowCreate)

  const switchBoardView = (archived: boolean) => {
    setSearchQuery('')
    navigate(archived ? '/boards/archived' : '/boards')
  }

  const filteredBoards = (boards ?? []).filter((board) => {
    if (!searchQuery.trim()) return true
    const query = searchQuery.toLowerCase()
    return (
      board.title.toLowerCase().includes(query) ||
      (board.description && board.description.toLowerCase().includes(query))
    )
  })

  return (
    <PageShell
      breadcrumbItems={showArchived
        ? [{ label: 'Dashboard', to: '/dashboard' }, { label: 'Boards', to: '/boards' }, { label: 'Archived boards' }]
        : [{ label: 'Dashboard', to: '/dashboard' }, { label: 'Boards' }]}
      hideHeader
      icon={LayoutGrid}
      title={showArchived ? 'Archived boards' : 'Boards'}
      description={showArchived
        ? 'Restore archived boards whenever you need them again.'
        : 'Manage your Kanban boards and track work across teams.'}
    >
      <div className="space-y-6">
        <div className="grid gap-3 border-b border-gray-200 pb-4 dark:border-gray-700 sm:grid-cols-[minmax(0,1fr)_minmax(20rem,42rem)_minmax(0,1fr)] sm:items-center">
          <div className="order-1 min-w-0">
            <h2 className="text-base font-semibold tracking-tight text-gray-900 dark:text-gray-100">{showArchived ? 'Archived boards' : 'Your boards'}</h2>
            <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
              {(boards ?? []).length} {(boards ?? []).length === 1 ? 'board' : 'boards'} {showArchived ? 'archived' : 'in your workspace'}
            </p>
          </div>

          <div className="relative order-2 w-full">
            <Search className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              type="search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search boards..."
              className="h-10 w-full rounded-xl border border-gray-300 bg-white py-2 pl-10 pr-9 text-sm text-gray-900 shadow-sm transition-all placeholder:text-gray-400 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:placeholder:text-gray-500"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 flex h-5 w-5 -translate-y-1/2 items-center justify-center rounded-full text-slate-400 transition-colors hover:bg-slate-200 hover:text-slate-700 dark:hover:bg-white/10 dark:hover:text-gray-200"
                aria-label="Clear board search"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>

          <div className="order-3 flex w-full items-center justify-end gap-2 sm:w-auto">
            <div className="group relative">
              <button
                type="button"
                onClick={() => switchBoardView(!showArchived)}
                aria-label={showArchived ? 'Back to active boards' : 'View archived boards'}
                className={cn(
                  'flex h-10 w-10 cursor-pointer items-center justify-center rounded-xl border transition-all focus:outline-none focus:ring-2 focus:ring-primary/25',
                  showArchived
                    ? 'border-primary/30 bg-primary/10 text-primary dark:border-primary/40 dark:bg-primary/15'
                    : 'border-gray-300 bg-white text-slate-500 shadow-sm hover:border-slate-400 hover:bg-slate-50 hover:text-slate-800 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:border-gray-500 dark:hover:bg-gray-700 dark:hover:text-white',
                )}
              >
                <Archive className="h-4 w-4" aria-hidden />
              </button>
              <span className="pointer-events-none absolute right-0 top-full z-20 mt-2 whitespace-nowrap rounded-lg bg-slate-900 px-2.5 py-1.5 text-[11px] font-medium text-white opacity-0 shadow-lg transition-opacity duration-150 group-hover:opacity-100 dark:bg-black">
                {showArchived ? 'Back to active boards' : 'Archived boards'}
              </span>
            </div>

            <Button
              type="button"
              variant="primary"
              onClick={() => setShowCreate(true)}
              className="h-10 rounded-xl px-4 shadow-sm"
            >
              <Plus className="h-4 w-4 shrink-0" aria-hidden />
              <span className="ml-2">Create board</span>
            </Button>
          </div>
        </div>

      {isLoading && (
        <SectionLoader variant="cards" label="Loading boards" />
      )}

      {error && (
        <div className="py-20 text-center">
          <p className="text-sm text-red-500">
            Error loading boards: {(error as Error).message}
          </p>
        </div>
      )}

        {!isLoading && !error && (!boards || boards.length === 0) && (
          <div className="flex flex-col items-center gap-4 rounded-2xl border border-dashed border-slate-300 bg-slate-50/60 px-6 py-20 text-center dark:border-white/15 dark:bg-white/[0.03]">
            <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-white text-slate-600 shadow-sm dark:bg-white/10 dark:text-gray-200">
              <LayoutGrid className="h-7 w-7" />
            </div>
            <div>
              <h3 className="text-base font-semibold text-slate-900 dark:text-white">{showArchived ? 'No archived boards' : 'Create your first board'}</h3>
              <p className="mt-1 text-sm text-slate-500 dark:text-gray-400">{showArchived ? 'Archived boards will appear here until you restore them.' : 'Organize work, assign members, and track progress in one place.'}</p>
            </div>
            {!showArchived && (
              <Button type="button" variant="primary" onClick={() => setShowCreate(true)} className="rounded-xl">
                <Plus className="mr-2 h-4 w-4" />
                Create board
              </Button>
            )}
          </div>
        )}

        {!isLoading && !error && boards && boards.length > 0 && filteredBoards.length === 0 && (
        <div className="flex flex-col items-center gap-2 rounded-2xl border border-dashed border-slate-300 py-16 text-center dark:border-white/15">
          <Search className="h-8 w-8 text-gray-400 dark:text-gray-500" />
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">No boards match "{searchQuery}"</h3>
          <p className="text-xs text-gray-500 dark:text-gray-400">Try searching with a different term.</p>
          <Button type="button" variant="secondary" size="sm" onClick={() => setSearchQuery('')} className="mt-2">
            Clear search
          </Button>
        </div>
      )}

        {!isLoading && !error && filteredBoards.length > 0 && (
          <section>
            <div className="mb-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <h3 className="text-sm font-semibold text-slate-800 dark:text-gray-100">All boards</h3>
                <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold text-slate-500 dark:bg-white/10 dark:text-gray-400">{filteredBoards.length}</span>
              </div>
              {searchQuery && <p className="text-xs text-slate-500 dark:text-gray-400">Results for “{searchQuery}”</p>}
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {filteredBoards.map((board, idx) => {
                const background = board.background?.trim()
                const hasPersistedBackground = typeof board.background === 'string'
                const hasCustomBackground = Boolean(background && background !== '#fff' && background !== '#ffffff')
                const populatedCreator = typeof board.createdBy === 'object' && board.createdBy !== null ? board.createdBy : null
                const creator = populatedCreator ?? (String(board.createdBy) === currentUser?._id ? currentUser : null)
                const creatorName = creator
                  ? `${creator.firstName ?? ''} ${creator.lastName ?? ''}`.trim() || creator.email
                  : 'Unknown member'
                const creatorInitials = creatorName
                  .split(/\s+/)
                  .filter(Boolean)
                  .slice(0, 2)
                  .map((part) => part[0])
                  .join('')
                  .toUpperCase() || 'BC'
                const creatorKey = creator?._id ?? String(board.createdBy)
                const createdDate = new Date(board.createdAt).toLocaleDateString('en-US', {
                  month: 'short', day: 'numeric', year: 'numeric',
                })
                return (
                  <button
                    key={board._id}
                    type="button"
                    onClick={() => {
                      if (showArchived) {
                        updateBoard.mutate({ id: board._id, payload: { isArchived: false }, silent: true })
                      } else {
                        navigate(`/boards/${board._id}`)
                      }
                    }}
                    className="group relative flex min-h-[238px] cursor-pointer flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white text-left shadow-[0_1px_2px_rgba(15,23,42,0.04)] transition-all duration-200 hover:-translate-y-0.5 hover:border-slate-300 hover:shadow-[0_14px_30px_rgba(15,23,42,0.12)] focus:outline-none focus:ring-4 focus:ring-slate-200/70 dark:border-white/10 dark:bg-[#252628] dark:hover:border-white/20 dark:hover:shadow-[0_14px_30px_rgba(0,0,0,0.25)] dark:focus:ring-white/10"
                  >
                    <div
                      className="relative h-[94px] shrink-0 overflow-hidden"
                      style={{ background: hasPersistedBackground ? (background || '#ffffff') : getDefaultBoardCover(idx) }}
                    >
                      <div className={cn('absolute inset-0', hasCustomBackground ? 'bg-black/10' : 'bg-white/10 dark:bg-black/10')} />
                      <div className="relative flex h-full items-start p-3.5">
                        <span className={cn('inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[10px] font-semibold backdrop-blur-sm', hasCustomBackground ? 'border-white/25 bg-black/15 text-white' : 'border-slate-900/10 bg-white/65 text-slate-700 dark:border-white/15 dark:bg-black/15 dark:text-gray-100')}>
                          {board.visibility === 'workspace' ? <Globe className="h-3 w-3" /> : <Lock className="h-3 w-3" />}
                          {board.visibility === 'workspace' ? 'Workspace' : 'Private'}
                        </span>
                      </div>
                    </div>

                    <div className="flex min-h-0 flex-1 flex-col p-4">
                      <h3 className="line-clamp-2 text-[15px] font-semibold leading-5 tracking-tight text-slate-900 dark:text-white">{board.title}</h3>
                      {board.description?.trim() ? (
                        <p title={board.description} className="mt-2 truncate text-xs leading-5 text-slate-500 dark:text-gray-400">
                          {board.description}
                        </p>
                      ) : (
                        <p className="mt-2 text-xs leading-5 text-slate-400 dark:text-gray-500">No description added yet.</p>
                      )}
                      <div className="mt-auto flex items-center justify-between gap-2 border-t border-slate-100 pt-3 text-[11px] font-medium text-slate-500 dark:border-white/10 dark:text-gray-400">
                        <span className="flex min-w-0 items-center gap-1.5" title={`Created by ${creatorName}`}>
                          {creator?.avatar ? (
                            <img src={creator.avatar} alt="" className="h-5 w-5 shrink-0 rounded-full object-cover" />
                          ) : (
                            <span className={cn('flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[7px] font-bold text-white', getAvatarColorClass(creatorKey))}>
                              {creatorInitials}
                            </span>
                          )}
                          <span className="truncate">{creatorName}</span>
                        </span>
                        <span className="inline-flex shrink-0 items-center gap-1" title={`Created ${createdDate}`}>
                          <Clock className="h-3 w-3" />
                          {createdDate}
                        </span>
                        {showArchived && (
                          <span className="inline-flex shrink-0 items-center gap-1 text-primary"><RotateCcw className="h-3.5 w-3.5" />Restore</span>
                        )}
                      </div>
                    </div>
                  </button>
                )
              })}
            </div>
          </section>
        )}
      </div>

      <CreateBoardModal open={showCreate} onClose={() => setShowCreate(false)} />
    </PageShell>
  )
}

BoardsPage.displayName = 'BoardsPage'
